import { styled } from "@linaria/react"
import {
  Box,
  CircularProgress,
  Dialog,
  Stack,
  Theme,
  Typography,
  useMediaQuery,
} from "@mui/material"
import { XCircle, XClose } from "@untitled-ui/icons-react"
import { closeSnackbar } from "notistack"
import { useEffect, useState } from "react"

import { RemoveOrderDetails } from "./RemoveOrderDetails"
import { RemoveOrderPendingForm } from "./RemoveOrderPendingForm"
import { RemoveOrderPreviewForm } from "./RemoveOrderPreviewForm"
import { RemoveOrderSuccessForm } from "./RemoveOrderSuccessForm"
import { OrderStatus, readBurnResult } from "../../ao/pool"
import { TokenInfo } from "../../ao/token"
import { SNACKBAR_TIMEOUT } from "../../settings"
import { NumberFont } from "../../theme"
import { parseBigIntAsNumber, formatTicker } from "../../utils/format"
import { retryable } from "../../utils/utils"
import Alert from "../Alert"
import Button from "../Button"
import { IconButton } from "../IconButton"
import { SuccessIcon } from "../SuccessIcon"

type RemoveOrderSnackbarProps = {
  lpToken: TokenInfo
  lpAmount: string
  lpAmountNormalized: bigint
  expectedTokenA: bigint
  expectedTokenB: bigint
  tokenA: TokenInfo
  tokenB: TokenInfo
  orderId: string
  poolId: string
  orderStatus: OrderStatus
  orderError?: string
  tokenAOutput?: bigint
  tokenBOutput?: bigint
}

export function RemoveOrderSnackbar(props: RemoveOrderSnackbarProps) {
  const {
    lpToken,
    lpAmount,
    lpAmountNormalized,
    expectedTokenA,
    expectedTokenB,
    tokenA,
    tokenB,
    orderId,
    poolId,
  } = props

  const [open, setOpen] = useState(false)

  function closePopover() {
    setOpen(false)
    if (orderError) {
      closeSnackbar(orderId)
    }
  }

  const [orderStatus, setOrderStatus] = useState<OrderStatus>(props.orderStatus)
  const [orderConfirmationId, setOrderConfirmationId] = useState<string>()
  const [orderError, setOrderError] = useState(props.orderError)
  const [tokenAOutput, setTokenAOutput] = useState(props.tokenAOutput)
  const [tokenBOutput, setTokenBOutput] = useState(props.tokenBOutput)

  useEffect(() => {
    ;(async function () {
      if (!orderId) return
      if (orderStatus !== "pending") {
        setTimeout(() => {
          closeSnackbar(orderId)
        }, SNACKBAR_TIMEOUT)
        return
      }

      try {
        const [withdrawals, confirmationId] = await retryable(readBurnResult)(orderId)
        setTokenAOutput(withdrawals[tokenA.id])
        setTokenBOutput(withdrawals[tokenB.id])
        setOrderConfirmationId(confirmationId)

        setTimeout(() => {
          closeSnackbar(orderId)
        }, SNACKBAR_TIMEOUT)
      } catch (error) {
        if (!(error instanceof Error)) {
          setOrderError(String(error))
          setOrderStatus("error")
          return
        }

        setOrderStatus("error")
        setOrderError(error.message)
      }
    })()
  }, [orderId, tokenA.id, tokenB.id, setTokenAOutput, setTokenBOutput, orderStatus])

  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"))

  return (
    <>
      <Stack
        onClick={() => setOpen(true)}
        direction="row"
        gap={2}
        alignItems="center"
        sx={{
          cursor: "pointer",
          margin: -1.5,
          paddingX: 2,
          paddingY: 1.5,
          minWidth: 320,
          maxWidth: 320,
          width: "100%",
        }}
      >
        <Box
          sx={{
            minWidth: 40,
            minHeight: 40,
            maxWidth: 40,
            maxHeight: 40,
            //
          }}
        >
          {orderStatus === "pending" ? (
            <CircularProgress color="inherit" thickness={6} />
          ) : orderStatus === "success" ? (
            <SuccessIcon width={40} height={40} />
          ) : (
            <XCircle
              style={{ color: "var(--mui-palette-error-main)", width: "100%", height: "100%" }}
            />
          )}
        </Box>
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{
            overflow: "hidden",
            wordBreak: "break-word",
          }}
        >
          {orderStatus === "success" ? "Withdrew" : "Withdrawing"}{" "}
          <Typography variant="inherit" component="span" color="text.primary">
            <Typography
              variant="inherit"
              component="span"
              fontFamily={NumberFont}
              sx={{ wordBreak: "break-all" }}
            >
              {parseBigIntAsNumber(
                lpAmountNormalized,
                lpToken.denomination,
                lpToken.denomination,
              )}{" "}
            </Typography>{" "}
            {formatTicker(lpToken.ticker)}
          </Typography>
        </Typography>
      </Stack>
      <Dialog
        fullScreen={isMobile}
        fullWidth
        maxWidth="sm"
        position="top"
        sx={{
          "& .MuiDialog-paper": {
            minHeight: 350,
            justifyContent: "center",
          },
        }}
        open={open}
        onClose={closePopover}
      >
        <IconButton
          onClick={closePopover}
          size="large"
          style={{
            position: "absolute",
            top: "20px",
            right: "20px",
            zIndex: 1,
          }}
        >
          <XClose />
        </IconButton>
        {orderStatus === "pending" ? (
          <RemoveOrderPendingForm
            lpAmountNormalized={lpAmountNormalized}
            expectedTokenA={expectedTokenA}
            expectedTokenB={expectedTokenB}
            lpToken={lpToken}
            tokenA={tokenA}
            tokenB={tokenB}
          />
        ) : orderStatus === "success" ? (
          <RemoveOrderSuccessForm
            lpAmountNormalized={lpAmountNormalized}
            tokenA={tokenA}
            tokenB={tokenB}
            tokenAOutput={tokenAOutput as bigint}
            tokenBOutput={tokenBOutput as bigint}
            lpToken={lpToken}
            poolId={poolId}
            orderConfirmationId={orderConfirmationId as string}
          />
        ) : (
          <Stack gap={2}>
            <RemoveOrderPreviewForm
              lpToken={lpToken as TokenInfo}
              lpAmount={lpAmount}
              tokenA={tokenA as TokenInfo}
              tokenB={tokenB as TokenInfo}
            />
            <div>
              <RemoveOrderDetails
                tokenA={tokenA}
                tokenB={tokenB}
                expectedTokenA={expectedTokenA}
                expectedTokenB={expectedTokenB}
                lpAmountNormalized={lpAmountNormalized}
              />
            </div>
            {orderError && (
              <Alert type="error" topSpace>
                {orderError}
              </Alert>
            )}
            {orderStatus === "error" && (
              <ContinueButton onClick={closePopover} className="secondary">
                Close
              </ContinueButton>
            )}
          </Stack>
        )}
      </Dialog>
    </>
  )
}

const ContinueButton = styled(Button)`
  padding: 1.4rem 1.55rem;
  width: 100%;
  font-size: 1.35rem;
`
